import { Controller } from "@hotwired/stimulus"

// Demo-only: types sample text character-by-character so a mock looks like it is
// being filled in live. Works on form fields (writes .value) and plain elements
// (writes .textContent). Starts when it scrolls into view; runs once.
export default class extends Controller {
  static values = {
    text: String,
    speed: { type: Number, default: 45 }, // base ms per character
    delay: { type: Number, default: 500 }, // pause before typing begins
    autostart: { type: Boolean, default: true }, // start when scrolled into view
  }

  connect() {
    this.cursor = 0
    this.started = false
    this.isField =
      this.element.tagName === "TEXTAREA" || this.element.tagName === "INPUT"
    this.write("")
    // When autostart is off, something else calls start() (e.g. after a scroll).
    if (this.autostartValue) {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this.start()
        },
        { threshold: 0.6 },
      )
      this.observer.observe(this.element)
    }
  }

  // Public: begin typing (after `delay`). Safe to call more than once.
  start() {
    if (this.started) return
    this.started = true
    this.observer?.disconnect()
    this.startTimer = setTimeout(() => this.type(), this.delayValue)
  }

  disconnect() {
    this.observer?.disconnect()
    clearTimeout(this.startTimer)
    clearTimeout(this.tickTimer)
  }

  type() {
    if (this.cursor >= this.textValue.length) {
      // Lets siblings react (e.g. a tag that fades in once typing is done).
      this.element.classList.add("typewriter-done")
      this.dispatch("done") // fires "typewriter:done" (bubbles)
      return
    }
    this.cursor += 1
    this.write(this.textValue.slice(0, this.cursor))
    // Jitter (and a longer beat after spaces) so the cadence feels human.
    // Scaled off speed so `speed` is the single knob controlling pace.
    const lastChar = this.textValue[this.cursor - 1]
    const jitter =
      this.speedValue + Math.random() * this.speedValue + (lastChar === " " ? this.speedValue * 1.8 : 0)
    this.tickTimer = setTimeout(() => this.type(), jitter)
  }

  write(value) {
    if (this.isField) {
      this.element.value = value
      // Behave like real typing so listeners (e.g. survey-mock) react.
      this.element.dispatchEvent(new Event("input", { bubbles: true }))
    } else {
      this.element.textContent = value
    }
  }
}
